import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { MobileTabBar } from '../components/MobileTabBar';
import {
  User, Settings, CreditCard, Users, Mail, Share2, Calendar,
  Shield, LogOut, ChevronRight, ArrowLeft, Sparkles
} from 'lucide-react';

const Row = ({ icon: Icon, label, hint, onClick, danger, testId }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-4 px-5 py-4 active:bg-gray-100 transition-colors ${
      danger ? 'text-red-600' : 'text-black'
    }`}
    data-testid={testId}
  >
    <span
      className={`flex items-center justify-center h-9 w-9 rounded-full ${
        danger ? 'bg-red-50' : 'bg-gray-100'
      }`}
    >
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
    </span>
    <span className="flex-1 text-left">
      <span className="block text-sm font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {label}
      </span>
      {hint && (
        <span className="block text-[11px] text-gray-400 mt-0.5">{hint}</span>
      )}
    </span>
    {!danger && <ChevronRight className="h-4 w-4 text-gray-300" />}
  </button>
);

const SectionLabel = ({ children }) => (
  <p
    className="px-5 pt-7 pb-2 text-[10px] tracking-[0.25em] uppercase text-gray-400 font-semibold"
    style={{ fontFamily: 'Outfit, sans-serif' }}
  >
    {children}
  </p>
);

export const MobileMenuPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [planType, setPlanType] = useState('free');
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  useEffect(() => {
    let cancelled = false;
    apiClient.get('/plan/current')
      .then((res) => { if (!cancelled) setPlanType(res.data?.plan || 'free'); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const firstName = user?.name?.split(' ')[0] || 'You';

  return (
    <div
      className="min-h-screen bg-white text-black md:hidden pb-24"
      style={{ fontFamily: 'Manrope, sans-serif' }}
      data-testid="mobile-menu-page"
    >
      {/* Header */}
      <header className="h-14 px-4 flex items-center justify-between border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur-xl z-30">
        <button
          onClick={() => navigate('/dashboard')}
          className="h-9 w-9 -ml-1 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
          data-testid="mobile-menu-back"
          aria-label="Back to dashboard"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          More
        </span>
        <span className="w-9" />
      </header>

      {/* Account summary */}
      <section className="px-5 pt-8 pb-4">
        <p className="text-[10px] tracking-[0.25em] uppercase text-gray-400">Signed in as</p>
        <h1
          className="mt-1 text-3xl font-light tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Hi, <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>{firstName}</span>.
        </h1>
        <p className="mt-1 text-xs text-gray-400">{user?.email}</p>
        <p className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0B0C10] text-white text-[10px] font-semibold tracking-wider uppercase">
          <Sparkles className="h-3 w-3" />
          {planType} plan
        </p>
      </section>

      <SectionLabel>Account</SectionLabel>
      <div className="bg-white border-y border-gray-100 divide-y divide-gray-100">
        <Row icon={User} label="Profile" onClick={() => navigate('/profile')} testId="menu-row-profile" />
        <Row icon={Settings} label="Account settings" onClick={() => navigate('/account-settings')} testId="menu-row-settings" />
        <Row icon={CreditCard} label="Subscription" hint={`Current plan: ${planType}`} onClick={() => navigate('/subscription')} testId="menu-row-subscription" />
        {['starter', 'pro', 'team'].includes(planType) && (
          <Row icon={Users} label="Team" onClick={() => navigate('/team')} testId="menu-row-team" />
        )}
      </div>

      <SectionLabel>Content</SectionLabel>
      <div className="bg-white border-y border-gray-100 divide-y divide-gray-100">
        <Row icon={Calendar} label="Scheduled posts" onClick={() => navigate('/scheduled-posts')} testId="menu-row-scheduled" />
        <Row icon={Mail} label="Email lists" onClick={() => navigate('/email-lists')} testId="menu-row-emaillists" />
        <Row icon={Share2} label="Social accounts" onClick={() => navigate('/social-accounts')} testId="menu-row-social" />
      </div>

      {isAdmin && (
        <>
          <SectionLabel>Admin</SectionLabel>
          <div className="bg-white border-y border-gray-100 divide-y divide-gray-100">
            <Row icon={Shield} label="Manage users" onClick={() => navigate('/admin/users')} testId="menu-row-admin-users" />
            <Row icon={Sparkles} label="Design Studio" onClick={() => navigate('/design-studio')} testId="menu-row-admin-design" />
          </div>
        </>
      )}

      <SectionLabel>Session</SectionLabel>
      <div className="bg-white border-y border-gray-100">
        <Row icon={LogOut} label="Log out" onClick={handleLogout} danger testId="menu-row-logout" />
      </div>

      <p className="text-center text-[11px] text-gray-300 mt-8" style={{ fontFamily: 'Playfair Display, serif' }}>
        RealProp · Marketing on autopilot.
      </p>

      <MobileTabBar />
    </div>
  );
};

export default MobileMenuPage;
